import React, { useEffect, useState, useCallback } from "react";
import { Formik } from "formik";
import * as Yup from "yup";
import { Link, useHistory, useParams } from "react-router-dom";

import Container from "../../../../../../components/Container";
import Layout from "../../../../../../components/Layout";
import Content from "../../../../../../components/Content";
import DropDownList from "../../../../../../components/DropDownList";
import Flex from "../../../../../../components/Flex";
import Label from "../../../../../../components/Label";
import TextBox from "../../../../../../components/TextBox";
import Button from "../../../../../../components/Button";
import ButtonList from "../../../../../../components/ButtonList";
import { useFetch } from "../../../../../../components/useFetch";
import Breadcrumbs from "../../../../../../components/Breadcrumbs";
import { useMessageToast } from "../../../../../../components/MessageProvider";
import { useAuthState } from "../../../../../../components/AuthProvider";
import errorMessageHandler from "../../../../errorMessageHandler";
import Spinner from "../../../../../../components/Loader/Spinner";

import styles from "./AddEditSubscription.module.scss";

const AddEditSubscription = () => {
  const moment = require("moment-timezone");
  const clientConfigApiDomain = process.env.REACT_APP_CLIENT_CONFIG_API_DOMAIN;
  const organizationApiUrl = `${clientConfigApiDomain}/organizations`;
  const clientsApiUrl = `${clientConfigApiDomain}/clients`;
  const subscriptionApiUrl = `${clientConfigApiDomain}/subscriptions`;
  const clientSubscriptionsApiUrl = `${clientConfigApiDomain}/client_subscriptions`;
  const matchModelsApiUrl = `${clientConfigApiDomain}/match_models`;

  const { id, clientId, subscriptionId } = useParams();

  const history = useHistory();
  const { user } = useAuthState();
  const toast = useMessageToast();

  const [
    { hasLoaded: addEditHasLoaded, hasError: addEditHasError, error },
    addEditFetch
  ] = useFetch();

  const [isLoading, setIsLoading] = useState(false);
  const [hasLoaded, setHasLoaded] = useState(false);
  const [clientSubscriptionData, setClientSubscriptionData] = useState([]);
  const [subscriptionData, setSubscriptionData] = useState([]);
  const [matchModelData, setMatchModelData] = useState([]);
  const [clientData, setClientData] = useState([]);
  const [organizationData, setOrganizationData] = useState([]);
  const [matchModelListData, setMatchModelListData] = useState([]);

  const fetchInitialData = useCallback(async () => {
    window.scrollTo(0, 0);
    setIsLoading(true);
    if (subscriptionId !== undefined) {
      // get client subscription
      const response = await fetch(
        `${clientSubscriptionsApiUrl}/${subscriptionId}/`
      );
      const data = await response.json();
      setClientSubscriptionData(data);
      // get subscription
      const respSub = await fetch(
        `${subscriptionApiUrl}/${data.subscription.id}/`
      );
      const dataSub = await respSub.json();
      setSubscriptionData(dataSub);
      // get match model
      const matchModelIdArray = data.match_model.split("/");
      const respMatchModel = await fetch(
        `${matchModelsApiUrl}/${
          matchModelIdArray[matchModelIdArray.length - 2]
        }/`
      );
      const dataMatchModel = await respMatchModel.json();
      setMatchModelData(dataMatchModel);
    }

    const clientResponse = await fetch(`${clientsApiUrl}/${clientId}/`);
    const clientData = await clientResponse.json();
    setClientData(clientData);

    const organizationResponse = await fetch(`${organizationApiUrl}/${id}/`);
    const organizationData = await organizationResponse.json();
    setOrganizationData(organizationData);

    const matchModelListResponse = await fetch(`${matchModelsApiUrl}/`);
    const matchModelListData = await matchModelListResponse.json();
    setMatchModelListData(matchModelListData.results);

    setIsLoading(false);
    setHasLoaded(true);
  }, [
    clientId,
    clientsApiUrl,
    id,
    organizationApiUrl,
    subscriptionId,
    clientSubscriptionsApiUrl,
    matchModelsApiUrl,
    subscriptionApiUrl
  ]);

  useEffect(() => {
    if (!hasLoaded) {
      fetchInitialData();
    } else {
      // this enables the add screen to show up
      setHasLoaded(true);
    }
  }, [subscriptionId, fetchInitialData, hasLoaded]);

  useEffect(() => {
    if (subscriptionId === undefined && addEditHasLoaded) {
      if (addEditHasLoaded) {
        toast.success(`Subscription added successfully.`);

        history.push(`/admin/organizations/${id}/accounts/${clientId}`);
      } else if (addEditHasError) {
        if (error.response.status === 400) {
          const errorMsgArray = errorMessageHandler(error.response.data);
          errorMsgArray.forEach(msg => toast.error(msg));
        } else toast.error(`Failed to add subscription.`);
      }
    } else {
      if (addEditHasLoaded) {
        toast.success(`Subscription updated successfully.`);

        history.push(
          `/admin/organizations/${id}/accounts/${clientId}/subscriptions/${subscriptionId}`
        );
      } else if (addEditHasError) {
        if (error.response.status === 400) {
          const errorMsgArray = errorMessageHandler(error.response.data);
          errorMsgArray.forEach(msg => toast.error(msg));
        } else toast.error(`Failed to update this subscription.`);
      }
    }
  }, [
    subscriptionId,
    addEditHasError,
    addEditHasLoaded,
    toast,
    history,
    id,
    clientId,
    error
  ]);

  let loadedBreadCrumbs = "";
  if (isLoading) {
    loadedBreadCrumbs = <Spinner />;
  } else if (hasLoaded) {
    loadedBreadCrumbs = (
      <Breadcrumbs>
        <Breadcrumbs.Item render={() => <Link to="/">Home</Link>} />
        <Breadcrumbs.Item
          render={() => <Link to="/admin">Administration</Link>}
        />
        <Breadcrumbs.Item
          render={() => <Link to="/admin/organizations">Organizations</Link>}
        />
        <Breadcrumbs.Item
          render={() => (
            <Link to={`/admin/organizations/${id}`}>
              Organization: {organizationData.name}
            </Link>
          )}
        />
        <Breadcrumbs.Item
          render={() => (
            <Link to={`/admin/organizations/${id}/accounts/${clientId}`}>
              Account: {clientData.name}
            </Link>
          )}
        />
        {subscriptionId ? (
          <>
            <Breadcrumbs.Item
              render={() => (
                <Link
                  to={`/admin/organizations/${id}/accounts/${clientId}/subscriptions/${subscriptionId}`}
                >
                  Subscription: {subscriptionData.name}
                </Link>
              )}
            />
            <Breadcrumbs.Item last render={() => "Edit"}></Breadcrumbs.Item>
          </>
        ) : (
          <Breadcrumbs.Item
            last
            render={() => "Add Subscription"}
          ></Breadcrumbs.Item>
        )}
      </Breadcrumbs>
    );
  }

  return (
    <Layout>
      <Container>
        <Flex gutter={3}>
          <Flex.Item span={12}>{loadedBreadCrumbs}</Flex.Item>
          {isLoading ? (
            <Spinner />
          ) : hasLoaded ? (
            <Flex.Item span={12}>
              <Content>
                <h1>
                  {subscriptionId
                    ? `Edit Subscription: ${subscriptionData.name}`
                    : `Add Subscription`}
                </h1>
              </Content>
              <div className={styles.base}>
                <div className={styles.detailsFlex}>
                  <h3>Subscription Details</h3>
                </div>
                <div className={styles.details}>
                  <Formik
                    initialValues={{
                      client: clientSubscriptionData.client,
                      subscription: subscriptionData.url,
                      matchModel: matchModelData.url,
                      s3BucketName: clientSubscriptionData.s3_bucket_name,
                      sinkKafkaTopicName:
                        clientSubscriptionData.sink_kafka_topic_name,
                      sourceKafkaTopicName:
                        clientSubscriptionData.source_kafka_topic_name,
                      entityChangesKafkaTopicName:
                        clientSubscriptionData.entity_changes_kafka_topic_name,
                      createdBy: clientSubscriptionData.created_by,
                      updatedBy: clientSubscriptionData.updated_by
                    }}
                    enableReinitialize={true}
                    validationSchema={Yup.object().shape({})}
                    onSubmit={(values, { setSubmitting, resetForm }) => {
                      setSubmitting(true);
                      let data = {
                        client: values.client,
                        subscription: values.subscription,
                        match_model: values.matchModel,
                        s3_bucket_name: values.s3BucketName,
                        sink_kafka_topic_name: values.sinkKafkaTopicName,
                        source_kafka_topic_name: values.sourceKafkaTopicName,
                        entity_changes_kafka_topic_name:
                          values.entityChangesKafkaTopicName,
                        updated_by: user.username,
                        created_by: values.createdBy
                      };
                      let method = "";
                      let url = "";

                      if (subscriptionId) {
                        data["id"] = subscriptionId;
                        method = "put";
                        url = `${clientSubscriptionsApiUrl}/${subscriptionId}/`;
                      } else {
                        data["created_by"] = user.username;
                        method = "post";
                        url = `${clientSubscriptionsApiUrl}/`;
                      }

                      addEditFetch({
                        method,
                        url,
                        data
                      });

                      setSubmitting(false);
                    }}
                  >
                    {props => {
                      const {
                        values,
                        touched,
                        errors,
                        handleChange,
                        handleBlur,
                        handleSubmit
                      } = props;

                      return (
                        <form onSubmit={handleSubmit} autoComplete="off">
                          <div>
                            {subscriptionId ? (
                              <>
                                <h4>Subscription ID</h4>
                                <span>{subscriptionData.id}</span>
                              </>
                            ) : null}
                          </div>
                          <div>
                            <DropDownList
                              errorText={errors.matchModel}
                              id="matchModel"
                              label={
                                <Label
                                  error={errors.matchModel}
                                  htmlFor="matchModel"
                                >
                                  <b>Match Model</b>
                                </Label>
                              }
                              onChange={handleChange}
                              onBlur={handleBlur}
                              value={values.matchModel}
                              width="25%"
                            >
                              {matchModelListData.map(matchModel => (
                                <DropDownList.Item
                                  key={matchModel.id}
                                  value={matchModel.url}
                                >
                                  {matchModel.name}
                                </DropDownList.Item>
                              ))}
                            </DropDownList>
                          </div>
                          <div>
                            <TextBox
                              errorText={
                                touched.s3BucketName
                                  ? errors.s3BucketName
                                  : null
                              }
                              id="s3BucketName"
                              label={
                                <Label
                                  error={
                                    touched.s3BucketName && errors.s3BucketName
                                  }
                                  htmlFor="s3BucketName"
                                >
                                  <b>S3 Bucket</b>
                                </Label>
                              }
                              onChange={handleChange}
                              onBlur={handleBlur}
                              required
                              value={values.s3BucketName}
                              width="25%"
                            />
                          </div>
                          <div>
                            <TextBox
                              errorText={
                                touched.sinkKafkaTopicName
                                  ? errors.sinkKafkaTopicName
                                  : null
                              }
                              id="sinkKafkaTopicName"
                              label={
                                <Label
                                  error={
                                    touched.sinkKafkaTopicName &&
                                    errors.sinkKafkaTopicName
                                  }
                                  htmlFor="sinkKafkaTopicName"
                                >
                                  <b>Sink Kafka Topic</b>
                                </Label>
                              }
                              onChange={handleChange}
                              onBlur={handleBlur}
                              required
                              value={values.sinkKafkaTopicName}
                              width="25%"
                            />
                          </div>
                          <div>
                            <TextBox
                              errorText={
                                touched.sourceKafkaTopicName
                                  ? errors.sourceKafkaTopicName
                                  : null
                              }
                              id="sourceKafkaTopicName"
                              label={
                                <Label
                                  error={
                                    touched.sourceKafkaTopicName &&
                                    errors.sourceKafkaTopicName
                                  }
                                  htmlFor="sourceKafkaTopicName"
                                >
                                  <b>Source Kafka Topic</b>
                                </Label>
                              }
                              onChange={handleChange}
                              onBlur={handleBlur}
                              required
                              value={values.sourceKafkaTopicName}
                              width="25%"
                            />
                          </div>
                          <div>
                            <TextBox
                              errorText={
                                touched.entityChangesKafkaTopicName
                                  ? errors.entityChangesKafkaTopicName
                                  : null
                              }
                              id="entityChangesKafkaTopicName"
                              label={
                                <Label
                                  error={
                                    touched.entityChangesKafkaTopicName &&
                                    errors.entityChangesKafkaTopicName
                                  }
                                  htmlFor="entityChangesKafkaTopicName"
                                >
                                  <b>Entity Changes Kafka Topic</b>
                                </Label>
                              }
                              onChange={handleChange}
                              onBlur={handleBlur}
                              required
                              value={values.entityChangesKafkaTopicName}
                              width="25%"
                            />
                          </div>
                          {subscriptionId ? (
                            <>
                              <div>
                                <h4>Created By</h4>
                                <span>{subscriptionData.created_by}</span>
                              </div>
                              <div>
                                <h4>Date Created</h4>
                                <span>
                                  {moment(subscriptionData.created_at)
                                    .tz(user.preferences.timeZone)
                                    .format("L, LTS z")}
                                </span>
                              </div>
                              <div>
                                <h4>Edited By</h4>
                                <span>{subscriptionData.updated_by}</span>
                              </div>
                              <div>
                                <h4>Date Updated</h4>
                                <span>
                                  {moment(subscriptionData.updated_at)
                                    .tz(user.preferences.timeZone)
                                    .format("L, LTS z")}
                                </span>
                              </div>
                            </>
                          ) : null}
                          <div>
                            <ButtonList>
                              <Button
                                primary
                                type="submit"
                                disabled={
                                  user.role === "Global Admin" ||
                                  user.orgIds.includes(organizationData.id)
                                    ? false
                                    : true
                                }
                              >
                                {subscriptionId ? `Save Changes` : `Save`}
                              </Button>
                              <Button
                                onClick={() => {
                                  history.goBack();
                                }}
                              >
                                Cancel
                              </Button>
                            </ButtonList>
                          </div>
                        </form>
                      );
                    }}
                  </Formik>
                </div>
              </div>
            </Flex.Item>
          ) : null}
        </Flex>
      </Container>
    </Layout>
  );
};

export default AddEditSubscription;
